import React, { useState } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import { makeStyles } from '@material-ui/core/styles';
import PageHeading from 'allGlobalComponents/PageHeading';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import ContactsIcon from '@mui/icons-material/Contacts';
import ArticleIcon from '@mui/icons-material/Article';
import OutboxIcon from '@mui/icons-material/Outbox';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Import tab components
import PushScheduleTab from './tabs/PushScheduleTab';
import ContactsTab from './tabs/ContactsTab';
import TemplatesTab from './tabs/TemplatesTab';
import OutboxTab from './tabs/OutboxTab';
import CompletedCampaignTab from './tabs/CompletedCampaignTab';

const useStyles = makeStyles(theme => ({
    // Remove the default gray header bar from FusePageSimple for this page only,
    // and inset it 16px so the heading lines up with the tabs/table below.
    header: {
        backgroundColor: 'transparent !important',
        padding: '0 16px'
    },
    contentContainer: {
        padding: theme.spacing(2)
    },
    tabsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : '#F3F4F6',
        borderRadius: 12,
        padding: 4,
        marginBottom: theme.spacing(3),
        width: 'fit-content',
        flexWrap: 'wrap'
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 3),
        borderRadius: 10,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        backgroundColor: 'transparent',
        border: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
        }
    },
    activeTab: {
        backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
        color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
        boxShadow: theme.palette.type === 'dark'
            ? '0 2px 8px rgba(0,0,0,0.3)'
            : '0 2px 8px rgba(0,0,0,0.08)',
        fontWeight: 600
    },
    tabIcon: {
        fontSize: 20
    },
    tabContent: {
        minHeight: 400
    }
}));

const TABS = [
    { id: 'templates', label: 'Templates', icon: ArticleIcon },
    { id: 'push-schedule', label: 'Push / Schedule Messages', icon: SendIcon },
    { id: 'contacts', label: 'Add Contacts', icon: ContactsIcon },
    { id: 'completed-campaign', label: 'Completed Campaign', icon: CheckCircleIcon },
    { id: 'outbox', label: 'Outbox', icon: OutboxIcon },
];

function EmailCampaign({ data = null }) {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState('templates');

    // Render active tab content
    const renderTabContent = () => {
        switch (activeTab) {
            case 'push-schedule':
                return <PushScheduleTab userData={data} />;
            case 'outbox':
                return <OutboxTab userData={data} />;
            case 'contacts':
                return <ContactsTab userData={data} />;
            case 'templates':
                return <TemplatesTab userData={data} />;
            case 'completed-campaign':
                return <CompletedCampaignTab userData={data} />;
            default:
                return <TemplatesTab userData={data} />;
        }
    };

    return (
        <FusePageSimple
            classes={{ header: classes.header }}
            header={<PageHeading icon={EmailIcon} title="Email Campaign" />}
            content={
                <div className={classes.contentContainer}>
                    {/* Tabs */}
                    <div className={classes.tabsContainer}>
                        {TABS.map((tab) => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    className={`${classes.tab} ${activeTab === tab.id ? classes.activeTab : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <IconComponent className={classes.tabIcon} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Tab Content */}
                    <div className={classes.tabContent}>
                        {renderTabContent()}
                    </div>
                </div>
            }
        />
    );
}

export default EmailCampaign;